import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { roleService, authService } from '../../services';

const RoleManagement = () => {
  // State for roles data
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [currentRole, setCurrentRole] = useState(null);
  
  // Form state
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Available permissions (this would typically come from your backend)
  const availablePermissions = [
    'users:read', 'users:write',
    'temples:read', 'temples:write',
    'hotels:read', 'hotels:write',
    'ghats:read', 'ghats:write',
    'bookings:read', 'bookings:write',
    'coupons:read', 'coupons:write',
    'roles:read', 'roles:write'
  ];

  // Check if user is admin
  const isAdmin = authService.hasRole('admin');
  
  // Fetch roles on component mount
  useEffect(() => {
    if (!isAdmin) {
      setError('You do not have permission to access this page.');
      return;
    }
    
    fetchRoles();
  }, [isAdmin]);
  
  // Fetch all roles
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const rolesData = await roleService.fetchAllRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open dialog for creating a new role
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions([]);
    setCurrentRole(null);
    setOpenDialog(true);
  };
  
  // Open dialog for editing an existing role
  const handleOpenEditDialog = (role) => {
    setDialogMode('edit');
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setSelectedPermissions(role.permissions || []);
    setCurrentRole(role);
    setOpenDialog(true);
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle permission selection change
  const handlePermissionChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedPermissions(
      typeof value === 'string' ? value.split(',') : value,
    );
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!roleName) {
      setError('Role name is required.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const roleData = {
        name: roleName,
        description: roleDescription,
        permissions: selectedPermissions
      };
      
      if (dialogMode === 'create') {
        await roleService.createRole(roleData);
      } else {
        await roleService.updateRole(currentRole.id, roleData);
      }
      
      // Refresh roles list
      await fetchRoles();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving role:', err);
      setError(err.response?.data?.message || 'Failed to save role. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle role deletion
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await roleService.deleteRole(roleId);
      // Refresh roles list
      await fetchRoles();
    } catch (err) {
      console.error('Error deleting role:', err);
      setError(err.response?.data?.message || 'Failed to delete role. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. You must be an admin to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Role Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={loading}
        >
          Add New Role
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No roles found.
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>{role.description || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {role.permissions && role.permissions.map((permission) => (
                          <Chip
                            key={permission}
                            label={permission}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {(!role.permissions || role.permissions.length === 0) && '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(role)}
                        disabled={role.name === 'admin' || role.name === 'user'}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteRole(role.id)}
                        disabled={role.name === 'admin' || role.name === 'user'}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Create/Edit Role Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Role' : 'Edit Role'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="roleName"
              label="Role Name"
              name="roleName"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              disabled={loading || (dialogMode === 'edit' && (currentRole?.name === 'admin' || currentRole?.name === 'user'))}
            />
            <TextField
              margin="normal"
              fullWidth
              id="roleDescription"
              label="Description"
              name="roleDescription"
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              disabled={loading}
              multiline
              rows={2}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="permissions-label">Permissions</InputLabel>
              <Select
                labelId="permissions-label"
                id="permissions"
                multiple
                value={selectedPermissions}
                onChange={handlePermissionChange}
                input={<OutlinedInput label="Permissions" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                disabled={loading || (dialogMode === 'edit' && (currentRole?.name === 'admin' || currentRole?.name === 'user'))}
              >
                {availablePermissions.map((permission) => (
                  <MenuItem key={permission} value={permission}>
                    <Checkbox checked={selectedPermissions.indexOf(permission) > -1} />
                    <ListItemText primary={permission} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !roleName || (dialogMode === 'edit' && (currentRole?.name === 'admin' || currentRole?.name === 'user'))}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
